import React, { useEffect, useRef, useState } from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { KnowledgeBaseFileDetails } from '@features/Admin/KnowledgeBase/ui/KnowledgeBaseFileDetails';
import type { CampaignFileSearchResponseDto } from '@shared/services/apiService/apiService';
import { knowledgeBaseApi } from '@shared/services/apiService/knowledgeBaseApi';
import { ConfirmDialog } from '@shared/components/confirmDialog/ConfirmDialog';
import {
  getKnowledgeBaseErrorMessageKey,
  KNOWLEDGE_BASE_PROCESSING_ERROR_KEY,
} from '@features/Admin/KnowledgeBase/model/error-mapper';
import {
  isKnowledgeBaseFileAvailable,
  isKnowledgeBaseFileReady,
} from '@features/Admin/KnowledgeBase/model/file-mapper';
import {
  validateKnowledgeBaseSearchQuery,
  type KnowledgeBaseSearchValidationError,
} from '@features/Admin/KnowledgeBase/model/search-validation';

import { useStyles } from '../KnowledgeBaseTab.styles';
import type { CampaignFileAgentScope } from '@features/Admin/KnowledgeBase/model/types';

import type { KnowledgeBaseFileDetailsDialogProps } from '../KnowledgeBaseTab.types';
import KnowledgeBaseAssignmentControls from './KnowledgeBaseAssignmentControls';
import KnowledgeBaseOriginalActions from './KnowledgeBaseOriginalActions';

const KnowledgeBaseFileDetailsDialog: React.FC<KnowledgeBaseFileDetailsDialogProps> = ({
  campaignId,
  file,
  agents,
  canManageFiles,
  canManageAssignments,
  isBusy,
  onSaveAssignments,
  onDelete,
  onClose,
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const [searchDocument, { isFetching: isSearchLoading }] =
    knowledgeBaseApi.useLazyCampaignFileSearchControllerSearchQuery();
  const [searchInput, setSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState<CampaignFileSearchResponseDto[]>([]);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [savedAgentScope, setSavedAgentScope] = useState<CampaignFileAgentScope>(file.agentScope);
  const [draftAgentScope, setDraftAgentScope] = useState<CampaignFileAgentScope>(file.agentScope);
  const [savedAgentIds, setSavedAgentIds] = useState<string[]>(file.agentIds);
  const [draftAgentIds, setDraftAgentIds] = useState<string[]>(file.agentIds);
  const [isAllAgentsConfirmOpen, setIsAllAgentsConfirmOpen] = useState(false);
  const searchRequestRef = useRef(0);
  const assignmentSnapshotRef = useRef({
    fileId: file.id,
    agentScope: file.agentScope,
    agentIds: file.agentIds,
  });
  const hasPendingAssignmentSyncRef = useRef(false);

  const isAssignmentsDirty =
    draftAgentScope !== savedAgentScope ||
    (draftAgentScope === 'selected' &&
      (draftAgentIds.length !== savedAgentIds.length ||
        draftAgentIds.some((agentId) => !savedAgentIds.includes(agentId))));
  const isSearchAvailable = isKnowledgeBaseFileReady(file);
  const isAvailable = isKnowledgeBaseFileAvailable(file);

  useEffect(() => {
    const previous = assignmentSnapshotRef.current;
    const isFileChanged = previous.fileId !== file.id;
    const areServerAssignmentsChanged =
      isFileChanged ||
      previous.agentScope !== file.agentScope ||
      previous.agentIds.length !== file.agentIds.length ||
      previous.agentIds.some((agentId, index) => agentId !== file.agentIds[index]);

    if (areServerAssignmentsChanged) {
      assignmentSnapshotRef.current = {
        fileId: file.id,
        agentScope: file.agentScope,
        agentIds: file.agentIds,
      };
      hasPendingAssignmentSyncRef.current = isFileChanged || isAssignmentsDirty;
    }

    if (hasPendingAssignmentSyncRef.current && !isAssignmentsDirty) {
      hasPendingAssignmentSyncRef.current = false;
      setSavedAgentScope(file.agentScope);
      setDraftAgentScope(file.agentScope);
      setSavedAgentIds(file.agentIds);
      setDraftAgentIds(file.agentIds);
    }
  }, [file.agentIds, file.agentScope, file.id, isAssignmentsDirty]);

  const saveAssignments = async () => {
    const agentIds = draftAgentScope === 'selected' ? draftAgentIds : [];
    const didSave = await onSaveAssignments(file, draftAgentScope, agentIds);

    if (didSave) {
      setSavedAgentScope(draftAgentScope);
      setSavedAgentIds(agentIds);
      setDraftAgentIds(agentIds);
    }
  };

  const handleSaveAssignments = () => {
    if (draftAgentScope === 'all' && savedAgentScope !== 'all') {
      setIsAllAgentsConfirmOpen(true);
      return;
    }

    void saveAssignments();
  };

  const handleConfirmAllAgentsAccess = async () => {
    setIsAllAgentsConfirmOpen(false);
    await saveAssignments();
  };

  const handleAgentScopeChange = (agentScope: CampaignFileAgentScope) => {
    setDraftAgentScope(agentScope);
    if (agentScope === 'all') setDraftAgentIds([]);
  };

  const selectedAgentNames = draftAgentIds.map(
    (agentId) => agents.find((agent) => agent.id === agentId)?.name ?? agentId,
  );

  const getSearchValidationMessageKey = (validationError: KnowledgeBaseSearchValidationError) =>
    `campaignPage.knowledgeBase.search.validation.${validationError}`;

  const handleSearchSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const requestId = ++searchRequestRef.current;
    const query = searchInput.trim();
    const validationError = validateKnowledgeBaseSearchQuery(query);

    setSearchResults([]);
    setSearchError(null);
    setHasSearched(false);

    if (validationError) {
      setSearchError(getSearchValidationMessageKey(validationError));
      return;
    }

    if (!isSearchAvailable) {
      setSearchError('campaignPage.knowledgeBase.search.notReady');
      return;
    }

    setHasSearched(true);

    try {
      const results = await searchDocument({ campaignId, fileId: file.id, query }).unwrap();

      if (requestId === searchRequestRef.current) {
        setSearchResults(results);
      }
    } catch (error) {
      if (requestId === searchRequestRef.current) {
        setSearchError(getKnowledgeBaseErrorMessageKey(error, 'search'));
      }
    }
  };

  return (
    <>
      <Dialog open onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>{t('campaignPage.knowledgeBase.details.title')}</DialogTitle>
        <DialogContent dividers>
          <KnowledgeBaseFileDetails
            file={file}
            isAvailable={isAvailable}
            stackProps={{ className: classes.detailsStack }}
            headerActions={
              <Stack direction="row" alignItems="center" gap={0.25}>
                <KnowledgeBaseOriginalActions
                  fileId={file.id}
                  fileName={file.fileName}
                  hasOriginal={file.hasOriginal}
                  canManageFiles={canManageFiles}
                />
                {canManageFiles && (
                  <Tooltip title={t('campaignPage.knowledgeBase.delete.action')}>
                    <span>
                      <IconButton
                        size="small"
                        aria-label={t('campaignPage.knowledgeBase.delete.action')}
                        onClick={() => onDelete(file)}
                        disabled={isBusy}
                        className={classes.deleteButton}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Stack>
            }>
            {canManageAssignments && (
              <>
                <KnowledgeBaseAssignmentControls
                  agents={agents}
                  agentScope={draftAgentScope}
                  selectedAgentIds={draftAgentIds}
                  disabled={isBusy}
                  onScopeChange={handleAgentScopeChange}
                  onChange={setDraftAgentIds}
                  onAssignAll={() => setDraftAgentIds(agents.map((agent) => agent.id))}
                />
              </>
            )}
            {!canManageAssignments && (
              <Stack gap={0.5}>
                <Typography variant="body2" fontWeight={600}>
                  {t('campaignPage.knowledgeBase.assignments.scopeLabel')}
                </Typography>
                <Typography variant="body2" color="text.secondary" role="status">
                  {file.agentScope === 'all'
                    ? t('campaignPage.knowledgeBase.assignments.allAgentsHint')
                    : file.agentIds.length > 0
                      ? selectedAgentNames.join(', ')
                      : t('campaignPage.knowledgeBase.assignments.noAgentAccess')}
                </Typography>
              </Stack>
            )}
            {file.status === 'failed' && (
              <Typography variant="body2" role="alert" className={classes.detailsError}>
                {t(KNOWLEDGE_BASE_PROCESSING_ERROR_KEY)}
              </Typography>
            )}
            <Box className={classes.searchSection}>
              <Box component="form" className={classes.searchForm} onSubmit={handleSearchSubmit}>
                <TextField
                  size="small"
                  fullWidth
                  label={t('campaignPage.knowledgeBase.search.label')}
                  placeholder={t('campaignPage.knowledgeBase.search.placeholder')}
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  disabled={!isSearchAvailable || isSearchLoading}
                  className={classes.documentSearchInput}
                  inputProps={{
                    maxLength: 1000,
                    'aria-label': t('campaignPage.knowledgeBase.search.label'),
                  }}
                  InputProps={{
                    startAdornment: <SearchIcon color="action" aria-hidden="true" />,
                  }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="small"
                  disabled={!isSearchAvailable || isSearchLoading}
                  className={classes.searchButton}
                  startIcon={
                    isSearchLoading ? (
                      <CircularProgress
                        size={16}
                        color="inherit"
                        aria-label={t('campaignPage.knowledgeBase.search.loading')}
                      />
                    ) : undefined
                  }>
                  {t('campaignPage.knowledgeBase.search.submit')}
                </Button>
              </Box>
              {!isSearchAvailable && (
                <Typography variant="body2" color="text.secondary" role="status">
                  {t('campaignPage.knowledgeBase.search.notReady')}
                </Typography>
              )}
              {searchError && (
                <Typography variant="body2" role="alert" className={classes.searchError}>
                  {t(searchError)}
                </Typography>
              )}
              {hasSearched && !isSearchLoading && !searchError && searchResults.length === 0 && (
                <Typography variant="body2" color="text.secondary" role="status">
                  {t('campaignPage.knowledgeBase.search.noResults')}
                </Typography>
              )}
              {searchResults.length > 0 && (
                <Stack
                  className={classes.searchResults}
                  aria-label={t('campaignPage.knowledgeBase.search.results')}>
                  {searchResults.map((result, index) => (
                    <Box key={`${result.chunkIndex}-${index}`} className={classes.searchResult}>
                      <Typography variant="body2" className={classes.searchResultFragment}>
                        {result.fragment}
                      </Typography>
                      <Box className={classes.searchResultMeta}>
                        <Typography
                          component="span"
                          variant="caption"
                          className={classes.searchResultScore}>
                          {t('campaignPage.knowledgeBase.search.score')}: {result.score.toFixed(1)}
                        </Typography>
                        <Typography component="span" variant="caption" color="text.secondary">
                          {t('campaignPage.knowledgeBase.search.page')}: {result.page ?? '—'}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          </KnowledgeBaseFileDetails>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('adminMainPage.operation.cancel')}</Button>
          {canManageAssignments && isAssignmentsDirty && (
            <Button variant="contained" onClick={handleSaveAssignments} disabled={isBusy}>
              {t('common.save')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        showDialogFlag={isAllAgentsConfirmOpen}
        handleClose={() => setIsAllAgentsConfirmOpen(false)}
        dialogTitle="campaignPage.knowledgeBase.assignments.allAgentsConfirmTitle"
        dialogContentText={t('campaignPage.knowledgeBase.assignments.allAgentsConfirmDescription')}
        showDialog={setIsAllAgentsConfirmOpen}
        confirmDialog={handleConfirmAllAgentsAccess}
        variant="warning"
      />
    </>
  );
};

export default KnowledgeBaseFileDetailsDialog;
